// DTI profile of ONE supplier: how its documents map onto Softline — the
// Manueller Artikel unmatched rows are booked on and the unit mappings. The
// backend serves it as two supplier-service resources (default-product,
// unit-mappings); `toSupplierProfile` is the only place that knows their shape.

export interface DtiManualProduct {
  id: string;
  /** Artikelnummer; '' when the product-service lookup failed. */
  key: string;
  description1: string | null;
}

/** Document unit text → Softline sales unit. There is no quantity factor:
    the backend maps the unit only, quantity and price stay as printed. */
export interface DtiUnitMapping {
  /** Unit text as printed on the supplier document ("stk", "Krt", "Pal"). */
  supplierUnit: string;
  unit: { id: string; key: string; name: string | null };
}

export interface DtiSupplierProfile {
  supplierId: string;
  /** The supplier's Manueller Artikel (customizable) — Diversartikel rows land here. */
  manualProduct: DtiManualProduct | null;
  unitMappings: DtiUnitMapping[];
}

/** `GET supplier-service/supplier/{id}/dti-profile/default-product` — the id only;
    key/description come from `GET product-service/products/{productId}`. */
export interface DtiDefaultProductResponse {
  productId: number | string;
}

/** One row of `GET supplier-service/supplier/{id}/dti-profile/unit-mappings`. */
export interface DtiUnitMappingResponse {
  /** Unit text as printed on the supplier document. */
  unit: string;
  internalUnitId: number | string;
  /** Softline unit key (e.g. "ST"). */
  internalUnit: string;
  internalUnitDescription: string | null;
}

/** Product id of the default-product response; null when none is configured. */
export function defaultProductId(
  response: DtiDefaultProductResponse | null | undefined
): string | null {
  const id = response?.productId;
  if (id == null || String(id) === '' || String(id) === '0') return null;
  return String(id);
}

/** Manueller Artikel from the id plus (when the lookup succeeded) its product record. */
export function toManualProduct(
  productId: string,
  product: { key?: string | null; description1?: string | null } | null | undefined
): DtiManualProduct {
  return {
    id: productId,
    key: product?.key ?? '',
    description1: product?.description1?.trim() || null,
  };
}

function toUnitMapping(mapping: DtiUnitMappingResponse): DtiUnitMapping | null {
  const supplierUnit = (mapping.unit ?? '').trim();
  const unitId = mapping.internalUnitId;
  if (!supplierUnit || unitId == null || String(unitId) === '') return null;
  return {
    supplierUnit,
    unit: {
      id: String(unitId),
      key: mapping.internalUnit ?? '',
      name: mapping.internalUnitDescription?.trim() || null,
    },
  };
}

/**
 * Builds the profile from the resolved Manueller Artikel and the unit-mappings
 * response. Null when the supplier has neither — that is "no DTI profile" for
 * the module. Rows without a unit text or a Softline unit id are dropped.
 */
export function toSupplierProfile(
  supplierId: string,
  manualProduct: DtiManualProduct | null,
  unitMappings: readonly DtiUnitMappingResponse[] | null | undefined
): DtiSupplierProfile | null {
  const mappings = (unitMappings ?? [])
    .map(toUnitMapping)
    .filter((m): m is DtiUnitMapping => m !== null);
  if (!manualProduct && mappings.length === 0) return null;
  return { supplierId, manualProduct, unitMappings: mappings };
}

/** Mapping for a document unit text (case-insensitive); null when none applies. */
export function findUnitMapping(
  profile: DtiSupplierProfile | null | undefined,
  unitText: string | null | undefined
): DtiUnitMapping | null {
  const needle = (unitText ?? '').trim().toLowerCase();
  if (!needle || !profile) return null;
  return (
    profile.unitMappings.find((m) => m.supplierUnit.toLowerCase() === needle) ?? null
  );
}
